import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './Automations.css';
import Menu from '../Shared/Menu';
import usePageTitle from '../Shared/usePageTitle';

const API_URL   = '/rest/sfmc/automations/v2';
const PAGE_SIZE = 10;

// Chip label → backend `filter` value. Backend matches on the literal status string.
const FILTER_MAP = {
    Paused:    'PausedSchedule',
    Scheduled: 'Scheduled',
    Active:    'Ready',
};

const STEP_ICON = {
    sql:     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6h16M4 10h16M4 14h10M4 18h6"/></svg>,
    data:    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
    import:  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
    filter:  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>,
    journey: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>,
    filetransfer: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>,
    script:  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>,
    wait:    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
};

/* ─── Helpers ─── */

function formatDate(dateStr) {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
    const isYesterday = d.toDateString() === yesterday.toDateString();
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    if (isToday) return `Today ${time}`;
    if (isYesterday) return `Yesterday ${time}`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' ' + time;
}

function formatDuration(ms) {
    if (!ms && ms !== 0) return '—';
    const totalSec = Math.round(ms / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}m ${s < 10 ? '0' : ''}${s}s`;
}

function normalizeStatus(raw) {
    if (!raw) return 'Paused';
    const s = raw.toLowerCase();
    // Check paused first — "PausedSchedule" should be Paused, not Scheduled
    if (s.includes('paused')) return 'Paused';
    // Active-like states: Ready, Active, Running
    if (s === 'active' || s === 'running' || s === 'ready') return 'Active';
    // Scheduled
    if (s === 'scheduled') return 'Scheduled';
    return 'Scheduled';
}

function mapStepType(activityType) {
    if (!activityType) return 'data';
    const t = activityType.toLowerCase();
    if (t.includes('query') || t.includes('sql')) return 'sql';
    if (t.includes('import')) return 'import';
    if (t.includes('filter')) return 'filter';
    if (t.includes('journey') || t.includes('interaction')) return 'journey';
    if (t.includes('transfer') || t.includes('extract') || t.includes('file')) return 'filetransfer';
    if (t.includes('script') || t.includes('ssjs')) return 'script';
    if (t.includes('wait')) return 'wait';
    return 'data';
}

function normalizeAutomation(item) {
    const status     = normalizeStatus(item.status);
    const activities = item.activities || [];
    const steps      = (item.steps || []).map(s => normalizeStep(s, activities));
    const executions = item.executions || [];
    const triggers   = item.triggers || [];
    const targetExts = item.targetExtensions || [];

    // Use pre-computed stats from API response, fall back to computing from executions
    let totalRuns, successRate, avgDuration;

    if (item.totalRuns != null) {
        totalRuns = item.totalRuns;
    } else {
        totalRuns = executions.length;
    }

    if (item.successRate != null) {
        successRate = item.successRate + '%';
    } else {
        const successRuns = executions.filter(e => (e.status || e.executionStatus) === 'Complete' || (e.status || e.executionStatus) === 'Success' || e.completedAt).length;
        successRate = totalRuns > 0 ? ((successRuns / totalRuns) * 100).toFixed(1) + '%' : 'N/A';
    }

    if (item.avgDuration != null) {
        avgDuration = item.avgDuration;
    } else {
        const durations = executions.filter(e => e.duration != null || e.executionDurationSeconds != null).map(e => e.duration != null ? e.duration : e.executionDurationSeconds * 1000);
        avgDuration = durations.length > 0 ? formatDuration(durations.reduce((a, b) => a + b, 0) / durations.length) : 'N/A';
    }

    return {
        ...item,
        status,
        subtitle: item.description || (item.scheduleType ? `Schedule: ${item.scheduleType}` : ''),
        stats: {
            lastRun:     item.lastRun ? formatDate(item.lastRun) : formatDate(item.lastRunAt),
            syncedAt:    formatDate(item.syncedAt),
            createdAt:   formatDate(item.createdAt),
            totalRuns,
            successRate,
            avgDuration,
        },
        steps,
        executions,
        activities,
        triggers,
        targetExtensions: targetExts,
    };
}

function normalizeStep(step, activities = []) {
    const linked = activities.filter(a => a.automationStepId === step.stepId);
    const primary = linked[0] || {};
    const type = mapStepType(step.activityType || step.type || primary.activityTypeName);

    let targetDE = step.targetDataExtension || step.targetDE || null;
    if (!targetDE && primary.targetDataExtensionsJson) {
        try {
            const parsed = JSON.parse(primary.targetDataExtensionsJson);
            if (Array.isArray(parsed) && parsed[0]?.name) targetDE = parsed[0].name;
        } catch (e) { /* ignore */ }
    }

    let duration = '—';
    if (primary.duration) duration = primary.duration;
    else if (step.duration != null) duration = formatDuration(step.duration);

    return {
        id:           step.id || step.stepId,
        name:         primary.activityName || step.name || step.stepName || step.activityType || 'Unnamed Step',
        type,
        label:        step.activityType || step.type || primary.activityTypeName || 'Step',
        rows:         step.rows || step.rowCount || step.targetRowCount || primary.rowCount || '—',
        duration,
        sequence:     step.sequence || step.stepNumber || step.stepSequence || 0,
        targetDE,
        activityId:   step.activityId || primary.activityId || null,
        activityType: step.activityType || step.type || primary.activityTypeName || '',
        details:      primary.details || '',
        viewSql:      primary.viewSql || primary.queryText || '',
        analyzeSql:   primary.analyzeSql || primary.viewSql || primary.queryText || '',
    };
}

function stepClasses(type) {
    const t = STEP_ICON[type] ? type : 'data';
    return {
        icon:   `step-icon step-icon-${t}`,
        badge:  `step-badge-${t}`,
        type:   `step-type step-type-${t}`,
        metric: `step-metric-value step-metric-value-${t}`,
        mobile: `step-metric-mobile-text step-type-${t}`,
    };
}

/* ─── Badge label per type ─── */
const STEP_BADGE_LABEL = {
    sql: 'SQL', data: 'Data Extract', import: 'Import File',
    filter: 'Filter', journey: 'Journey Entry',
    filetransfer: 'File Transfer', script: 'Script', wait: 'Wait',
};

/* ─── Step Icon Emoji ─── */
const STEP_EMOJI = {
    sql: '⌥', data: '◈', import: '↡', filter: '⚡', journey: '▶',
    filetransfer: '📄', script: '⟨/⟩', wait: '⏱',
};

/* ─── Step Detail Panel (slideout) ─── */
function StepDetailPanel({ step, onClose }) {
    const [activeTab, setActiveTab] = useState('details');
    if (!step) return null;

    const cls = stepClasses(step.type);
    const typeLabel = STEP_BADGE_LABEL[step.type] || step.label;

    return (
        <>
            <div className="step-panel-backdrop show" onClick={onClose} />
            <div className="step-panel show">
                {/* Header */}
                <div className="step-panel-header">
                    <div className="d-flex align-items-start justify-content-between w-100 mb-3">
                        <div className="d-flex align-items-center gap-3">
                            <div className={`step-icon modal-step-icon step-icon-${step.type}`}>
                                {STEP_EMOJI[step.type] || '◈'}
                            </div>
                            <div>
                                <div className="d-flex align-items-center gap-2 mb-1">
                                    <span className={`${cls.type} text-uppercase`} style={{ fontWeight: 700 }}>{typeLabel}</span>
                                    <span className="status-badge-success">
                                        <span className="status-dot-success" />
                                        Success
                                    </span>
                                </div>
                                <h2 className="step-panel-title">{step.name}</h2>
                            </div>
                        </div>
                        <button className="step-panel-close" onClick={onClose}>✕</button>
                    </div>

                    {/* Quick Stats */}
                    <div className="modal-quick-stats">
                        <div className="modal-stat-box">
                            <p className="modal-stat-label">Rows Processed</p>
                            <p className={`modal-stat-value modal-stat-value-${step.type}`}>{step.rows}</p>
                        </div>
                        <div className="modal-stat-box">
                            <p className="modal-stat-label">Duration</p>
                            <p className="modal-stat-value modal-stat-value-default">{step.duration}</p>
                        </div>
                        <div className="modal-stat-box">
                            <p className="modal-stat-label">Status</p>
                            <p className="modal-stat-value modal-stat-value-success">Success</p>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="step-panel-tabs d-flex">
                    <button className={`modal-tab${activeTab === 'details' ? ' active' : ''}`} onClick={() => setActiveTab('details')}>Details</button>
                    <button className={`modal-tab${activeTab === 'sql' ? ' active' : ''}`} onClick={() => setActiveTab('sql')}>View SQL</button>
                    <button className={`modal-tab${activeTab === 'analyze' ? ' active' : ''}`} onClick={() => setActiveTab('analyze')}>✨ Analyze SQL</button>
                </div>

                {/* Tab Content */}
                <div className="step-panel-body">
                    {activeTab === 'details' && (
                        <>
                            <div className="detail-section">
                                <p className="detail-section-title">Description</p>
                                <div className="detail-section-content">
                                    {step.details || step.description || `${typeLabel} step in the automation workflow.`}
                                </div>
                            </div>
                            <div className="detail-section">
                                <p className="detail-section-title">Query Details</p>
                                <div className="detail-table">
                                    {step.targetDE && (
                                        <div className="detail-table-row">
                                            <span className="detail-table-label">Target Data Extension</span>
                                            <span className="detail-table-value">{step.targetDE}</span>
                                        </div>
                                    )}
                                    <div className="detail-table-row">
                                        <span className="detail-table-label">Rows Written</span>
                                        <span className="detail-table-value">{step.rows}</span>
                                    </div>
                                    <div className="detail-table-row">
                                        <span className="detail-table-label">Activity Type</span>
                                        <span className="detail-table-value">{step.activityType || typeLabel}</span>
                                    </div>
                                    {step.activityId && (
                                        <div className="detail-table-row">
                                            <span className="detail-table-label">Activity ID</span>
                                            <span className="detail-table-value" style={{ fontSize: 11, fontFamily: 'monospace' }}>{step.activityId}</span>
                                        </div>
                                    )}
                                    <div className="detail-table-row">
                                        <span className="detail-table-label">Duration</span>
                                        <span className="detail-table-value">{step.duration}</span>
                                    </div>
                                    <div className="detail-table-row">
                                        <span className="detail-table-label">Sequence</span>
                                        <span className="detail-table-value">Step {step.sequence || 1}</span>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {activeTab === 'sql' && (
                        <div>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <p className="detail-section-title" style={{ margin: 0 }}>SQL Query</p>
                                <button className="copy-sql-btn" onClick={() => navigator.clipboard.writeText(step.viewSql || step.sql || '')}>Copy SQL</button>
                            </div>
                            <div className="sql-viewer">
                                <pre className="sql-code">{step.viewSql || step.sql || 'No SQL available for this step.'}</pre>
                            </div>
                        </div>
                    )}

                    {activeTab === 'analyze' && (
                        <div>
                            <div className="analyze-cta">
                                <div className="analyze-icon">🔬</div>
                                <p className="analyze-title">AI SQL Analyzer</p>
                                <p className="analyze-description">Instantly extract tables, fields, joins, filters and complexity from this query.</p>
                                <button className="analyze-btn">✨ Analyze SQL</button>
                            </div>
                            {step.analyzeSql && (
                                <div style={{ marginTop: 16 }}>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <p className="detail-section-title" style={{ margin: 0 }}>Query to analyze</p>
                                        <button className="copy-sql-btn" onClick={() => navigator.clipboard.writeText(step.analyzeSql)}>Copy SQL</button>
                                    </div>
                                    <div className="sql-viewer">
                                        <pre className="sql-code">{step.analyzeSql}</pre>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

/* ─── Step Row (matches HTML design) ─── */
function StepRow({ step, isLast, onStepClick }) {
    const cls = stepClasses(step.type);
    return (
        <div className="d-flex gap-0">
            <div className="d-flex flex-column align-items-center step-col">
                <div className={cls.icon}>{STEP_ICON[step.type] || STEP_ICON.data}</div>
                {!isLast && <div className="timeline-line" />}
            </div>
            <div className={`flex-grow-1 ms-3${isLast ? '' : ' mb-3'}`}>
                <div className="step-card" onClick={() => onStepClick && onStepClick(step)}>
                    <div className="d-flex justify-content-between align-items-start">
                        <div className="step-details-left">
                            <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                <p className="step-name">{step.name}</p>
                                {step.type === 'sql' && <span className={cls.badge}>SQL</span>}
                            </div>
                            <span className={cls.type}>{STEP_BADGE_LABEL[step.type] || step.label}</span>
                        </div>
                        <div className="step-metrics d-flex align-items-center gap-3 flex-shrink-0 ms-3">
                            <div className="text-end">
                                <p className={cls.metric}>{step.rows}</p>
                                <p className="step-metric-label">rows</p>
                            </div>
                            <div className="text-end">
                                <p className="step-duration">{step.duration}</p>
                                <p className="step-metric-label">duration</p>
                            </div>
                            <span className="status-badge-success">
                                <span className="status-dot-success" />
                                Success
                            </span>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="2.5">
                                <path d="M9 18l6-6-6-6" />
                            </svg>
                        </div>
                    </div>
                    <div className="step-metrics-mobile">
                        <div className={cls.mobile}>
                            {step.rows !== '—' ? `${step.rows} rows` : ''}
                            {step.rows !== '—' && step.duration !== '—' ? ' · ' : ''}
                            {step.duration !== '—' ? step.duration : ''}
                        </div>
                        <span className="status-badge-success">
                            <span className="status-dot-success" />
                            Success
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ─── Execution Row ─── */
function ExecutionRow({ execution }) {
    const isSuccess = execution.status === 'Complete' || execution.status === 'Success';
    return (
        <div className="d-flex align-items-center gap-3" style={{ padding: '8px 0', borderBottom: '1px solid #F1F5F9' }}>
            <span style={{
                width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                background: isSuccess ? '#10B981' : execution.status === 'Error' ? '#EF4444' : '#F59E0B',
            }} />
            <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#0F172A' }}>
                    {execution.status || 'Unknown'}
                </span>
                {execution.completedAt && (
                    <span style={{ fontSize: 11, color: '#94A3B8', marginLeft: 8 }}>
                        {formatDate(execution.completedAt)}
                    </span>
                )}
                {execution.startedAt && !execution.completedAt && (
                    <span style={{ fontSize: 11, color: '#94A3B8', marginLeft: 8 }}>
                        Started {formatDate(execution.startedAt)}
                    </span>
                )}
            </div>
            {execution.duration != null && (
                <span style={{ fontSize: 11, fontWeight: 600, color: '#6366F1', flexShrink: 0 }}>
                    {formatDuration(execution.duration)}
                </span>
            )}
        </div>
    );
}

/* ─── Raw API Response (collapsible) ─── */
function RawResponse({ automation }) {
    const [open, setOpen] = useState(false);
    // Build raw object excluding normalized fields
    const raw = { ...automation };
    delete raw.subtitle;
    delete raw.stats;

    return (
        <div style={{ marginTop: 16 }}>
            <button
                onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
                style={{
                    fontSize: 11, fontWeight: 700, color: '#64748B',
                    background: '#F8FAFC', border: '1px solid #E2E8F0',
                    borderRadius: 8, padding: '6px 12px', cursor: 'pointer',
                    textTransform: 'uppercase', letterSpacing: '0.07em',
                }}
            >
                {open ? '▾ Hide' : '▸ Show'} API Response
            </button>
            {open && (
                <pre style={{
                    marginTop: 8, background: '#0F172A', color: '#86EFAC',
                    borderRadius: 12, padding: '14px 16px', fontSize: 11,
                    fontFamily: 'Monaco, Menlo, Consolas, monospace',
                    overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    maxHeight: 400, overflowY: 'auto',
                }}>
                    {JSON.stringify(raw, null, 2)}
                </pre>
            )}
        </div>
    );
}

/* ─── Automation Card ─── */
function AutomationCard({ automation, expanded, onToggle, onStepClick }) {
    const isActive    = automation.status === 'Active';
    const isPaused    = automation.status === 'Paused';
    const accentClass = isActive ? 'accent-active' : isPaused ? 'accent-scheduled' : 'accent-scheduled';
    const badgeClass  = isActive ? 'status-badge-active' : isPaused ? 'status-badge-scheduled' : 'status-badge-scheduled';
    const dotClass    = isActive ? 'status-dot-active' : 'status-dot-scheduled';
    const stats       = automation.stats || {};
    const steps       = automation.steps || [];
    const executions  = automation.executions || [];
    const activities  = automation.activities || [];
    const triggers    = automation.triggers || [];
    const targetExts  = automation.targetExtensions || [];

    return (
        <div className="automation-card">
            <div
                className={`automation-header${expanded ? ' expanded' : ''}`}
                onClick={onToggle}
            >
                <div className={`accent-stripe ${accentClass}`} />
                <div className="automation-header-content">
                    <div className="d-flex align-items-start justify-content-between">
                        <div className="flex-grow-1">
                            <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                <h3 className="automation-title">{automation.name}</h3>
                                <span className={badgeClass}>
                                    <span className={dotClass} />
                                    {automation.status}
                                </span>
                            </div>
                            {automation.subtitle && <p className="automation-subtitle">{automation.subtitle}</p>}
                            <div className="automation-stats d-flex gap-4 flex-wrap">
                                <div className="stat-item">
                                    <p className="stat-label">Success Rate</p>
                                    <p className="stat-value stat-value-success">{stats.successRate || 'N/A'}</p>
                                </div>
                                <div className="stat-item">
                                    <p className="stat-label">Avg Duration</p>
                                    <p className="stat-value stat-value-primary">{stats.avgDuration || 'N/A'}</p>
                                </div>
                                <div className="stat-item">
                                    <p className="stat-label">Total Runs</p>
                                    <p className="stat-value stat-value-default">{stats.totalRuns || 0}</p>
                                </div>
                                <div className="stat-item">
                                    <p className="stat-label">Last Run</p>
                                    <p className="stat-value stat-value-default">{stats.lastRun || 'N/A'}</p>
                                </div>
                                <div className="stat-item">
                                    <p className="stat-label">Synced At</p>
                                    <p className="stat-value stat-value-default">{stats.syncedAt || 'N/A'}</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex align-items-center gap-2 flex-shrink-0 ms-3">
                            <div className="steps-badge-count text-center">
                                <p className="steps-badge-label">Steps</p>
                                <p className="steps-badge-value">{steps.length}</p>
                            </div>
                            <svg
                                className={`chevron-icon${expanded ? ' rotated' : ''}`}
                                width="16" height="16" viewBox="0 0 24 24"
                                fill="none" stroke="#CBD5E1" strokeWidth="2.5"
                            >
                                <path d="M9 18l6-6-6-6" />
                            </svg>
                        </div>
                    </div>
                </div>
            </div>

            <div className={`steps-container${expanded ? ' show' : ''}`}>
                {/* Steps timeline (matches HTML design) */}
                {steps.length > 0 && (
                    <div className="d-flex flex-column gap-0">
                        {steps
                            .sort((a, b) => a.sequence - b.sequence)
                            .map((step, i) => (
                                <StepRow key={step.id || i} step={step} isLast={i === steps.length - 1} onStepClick={onStepClick} />
                            ))}
                    </div>
                )}

                {/* Empty expanded state */}
                {steps.length === 0 && (
                    <p style={{ fontSize: 12, color: '#CBD5E1', textAlign: 'center', padding: 16, margin: 0 }}>
                        No steps data available.
                    </p>
                )}
            </div>
        </div>
    );
}

/* ─── Main Automations Page ─── */
function Automations() {
    usePageTitle('Automations');
    const [automations,  setAutomations]  = useState([]);
    const [loading,      setLoading]      = useState(true);
    const [error,        setError]        = useState(null);
    const [activeFilter, setActiveFilter] = useState('All');
    const [searchQuery,  setSearchQuery]  = useState('');
    const [expandedIds,  setExpandedIds]  = useState(new Set());
    const [selectedStep, setSelectedStep] = useState(null);
    const [page,         setPage]         = useState(1);
    const [hasMore,      setHasMore]      = useState(true);
    const [loadingMore,  setLoadingMore]  = useState(false);
    const [totalCount,   setTotalCount]   = useState(0);
    const [kpis,         setKpis]         = useState({ totalSteps: null, sqlQueries: null, totalRuns: null });
    const [refreshing,   setRefreshing]   = useState(false);
    const [lastRequest,  setLastRequest]  = useState(null);
    const sentinelRef = useRef(null);
    const loadingRef  = useRef(false);
    const firstLoadDoneRef = useRef(false);
    const pageRef     = useRef(1);
    const hasMoreRef  = useRef(true);

    // Reads the URL off the config axios actually sent, so the debug block shows
    // the mid the interceptor appended (absent on the default BU) rather than a
    // URL rebuilt here that could drift from the real request.
    const captureRequest = (config, extra) => {
        setLastRequest({
            url:  config ? axios.getUri(config) : '(request not sent)',
            time: new Date().toLocaleTimeString(),
            ...extra,
        });
    };

    const fetchPage = (p, opts = {}) => {
        if (loadingRef.current) return;
        loadingRef.current = true;
        if (p === 1) {
            if (firstLoadDoneRef.current) setRefreshing(true);
            else setLoading(true);
        } else {
            setLoadingMore(true);
        }
        const filterArg = opts.filter ?? activeFilter;
        const searchArg = opts.search ?? searchQuery;
        const params = new URLSearchParams({ page: String(p), pageSize: String(PAGE_SIZE) });
        if (searchArg) params.set('search', searchArg);
        if (filterArg && filterArg !== 'All') {
            params.set('filter', FILTER_MAP[filterArg] || filterArg);
        }
        axios.get(`${API_URL}?${params.toString()}`)
            .then(res => {
                const data = res.data;
                captureRequest(res.config, {
                    status:  res.status,
                    mid:     data?.mid,
                    total:   data?.total,
                    records: (data?.items || []).length,
                });
                console.log('[Automations] page', p, 'response:', data);
                const raw    = data?.items || [];
                const list   = raw.map(normalizeAutomation);
                const total  = typeof data?.total === 'number' ? data.total : null;
                const size   = typeof data?.pageSize === 'number' ? data.pageSize : PAGE_SIZE;

                const isUnfiltered = !searchArg && (!filterArg || filterArg === 'All');
                if (total != null && isUnfiltered) setTotalCount(total);
                setKpis({
                    totalSteps: typeof data?.totalSteps === 'number' ? data.totalSteps : null,
                    sqlQueries: typeof data?.sqlQueries === 'number' ? data.sqlQueries : null,
                    totalRuns:  typeof data?.totalRuns  === 'number' ? data.totalRuns  : null,
                });
                const more = total != null ? (p * size) < total : raw.length === size;
                hasMoreRef.current = more;
                pageRef.current = p;
                setHasMore(more);

                setAutomations(prev => {
                    const next = p === 1 ? list : [...prev, ...list];
                    if (p === 1 && list.length > 0) setExpandedIds(new Set([list[0].id]));
                    return next;
                });
            })
            .catch(err => {
                captureRequest(err.config, {
                    status: err.response?.status ?? 'network error',
                    failed: true,
                });
                setError(err.message);
            })
            .finally(() => {
                loadingRef.current = false;
                if (p === 1) {
                    setLoading(false);
                    setRefreshing(false);
                    firstLoadDoneRef.current = true;
                } else {
                    setLoadingMore(false);
                }
            });
    };

    useEffect(() => {
        const t = setTimeout(() => {
            pageRef.current = 1;
            hasMoreRef.current = true;
            setPage(1);
            setHasMore(true);
            loadingRef.current = false;
            fetchPage(1, { filter: activeFilter, search: searchQuery });
        }, searchQuery ? 300 : 0);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFilter, searchQuery]);

    const toggleAccordion = (id) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    useEffect(() => {
        const node = sentinelRef.current;
        if (!node) return;
        if (!hasMore || loadingMore) return;
        const io = new IntersectionObserver(entries => {
            if (!entries[0]?.isIntersecting) return;
            if (loadingRef.current || !hasMoreRef.current) return;
            const next = pageRef.current + 1;
            setPage(next);
            fetchPage(next, { filter: activeFilter, search: searchQuery });
        }, { rootMargin: '300px' });
        io.observe(node);
        return () => io.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasMore, loadingMore, automations.length, activeFilter, searchQuery]);

    const totalSteps   = automations.reduce((sum, a) => sum + (a.steps ? a.steps.length : 0), 0);
    const sqlQueries   = automations.reduce((sum, a) => sum + (a.steps ? a.steps.filter(s => s.type === 'sql').length : 0), 0);
    const activeCount  = automations.filter(a => a.status === 'Active').length;
    const totalExecs   = automations.reduce((sum, a) => sum + (a.executions ? a.executions.length : 0), 0);

    return (
        <>
            <Menu />

            <div className="container fade-up container-main">
                <div className="page-header mb-4">
                    <h1>Automations</h1>
                    <p>All automation workflows, steps, and SQL queries across your Marketing Cloud instance</p>
                </div>

                {/* Debug: the request behind the list on screen, mid included. */}
                <details className="auto-debug" open>
                    <summary className="auto-debug-summary">Debug · last request</summary>
                    {lastRequest ? (
                        <>
                            <code className="auto-debug-url">{lastRequest.url}</code>
                            <div className="auto-debug-meta">
                                <span className={lastRequest.failed ? 'is-fail' : 'is-ok'}>
                                    {lastRequest.status}
                                </span>
                                <span>mid: {lastRequest.mid ?? '— (default BU)'}</span>
                                {lastRequest.records != null && (
                                    <span>records: {lastRequest.records} of {lastRequest.total ?? '—'}</span>
                                )}
                                <span>{lastRequest.time}</span>
                            </div>
                        </>
                    ) : (
                        <code className="auto-debug-url">Waiting for the first request…</code>
                    )}
                </details>

                {loading && <div className="text-center py-5"><p>Loading automations...</p></div>}
                {error && <div className="text-center py-5"><p className="text-danger">Failed to load automations: {error}</p></div>}

                {!loading && !error && <>
                {/* KPI Tiles */}
                <div className="row g-3 mb-4">
                    <div className="col-6 col-md-3">
                        <div className="kpi-card d-flex align-items-start gap-3">
                            <div className="kpi-icon kpi-icon-automation">⚙</div>
                            <div>
                                <p className="kpi-label">Total Automations</p>
                                <p className="kpi-value">{totalCount || automations.length}</p>
                                <p className="kpi-subtext">{activeCount} active{totalCount > automations.length ? ` · ${automations.length} loaded` : ''}</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-6 col-md-3">
                        <div className="kpi-card d-flex align-items-start gap-3">
                            <div className="kpi-icon kpi-icon-steps">◈</div>
                            <div>
                                <p className="kpi-label">Total Steps</p>
                                <p className="kpi-value">{kpis.totalSteps ?? totalSteps}</p>
                                <p className="kpi-subtext">across all automations</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-6 col-md-3">
                        <div className="kpi-card d-flex align-items-start gap-3">
                            <div className="kpi-icon kpi-icon-sql">⌥</div>
                            <div>
                                <p className="kpi-label">SQL Queries</p>
                                <p className="kpi-value">{kpis.sqlQueries ?? sqlQueries}</p>
                                <p className="kpi-subtext">analyzable queries</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-6 col-md-3">
                        <div className="kpi-card d-flex align-items-start gap-3">
                            <div className="kpi-icon kpi-icon-runs">▶</div>
                            <div>
                                <p className="kpi-label">Total Runs</p>
                                <p className="kpi-value">{kpis.totalRuns ?? totalExecs}</p>
                                <p className="kpi-subtext">all time</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="d-flex flex-wrap gap-3 mb-4 align-items-center">
                    <div className="search-wrapper">
                        <svg className="search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2">
                            <circle cx="11" cy="11" r="8" />
                            <path d="m21 21-4.35-4.35" />
                        </svg>
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Search automations…"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <div className="filter-buttons d-flex gap-1">
                        {['All', 'Active', 'Scheduled', 'Paused'].map(f => (
                            <button
                                key={f}
                                className={`filter-btn${activeFilter === f ? ' active' : ''}`}
                                onClick={() => setActiveFilter(f)}
                            >
                                {f}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Results count */}
                <p style={{ fontSize: 12, color: '#94A3B8', marginBottom: 12 }}>
                    Showing <strong style={{ color: '#0F172A' }}>{automations.length}</strong> Records
                </p>

                {/* Automations List */}
                {automations.length > 0 ? (
                    <div className="d-flex flex-column gap-3">
                        {automations.map(a => (
                            <AutomationCard
                                key={a.id}
                                automation={a}
                                expanded={expandedIds.has(a.id)}
                                onToggle={() => toggleAccordion(a.id)}
                                onStepClick={setSelectedStep}
                            />
                        ))}
                        {hasMore && (
                            <div ref={sentinelRef} className="text-center py-3" style={{ fontSize: 12, color: '#94A3B8' }}>
                                {loadingMore ? 'Loading more…' : `Loaded ${automations.length} so far`}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="empty-state">
                        <div className="empty-state-icon">🔭</div>
                        <h3 className="empty-state-title">No automations found</h3>
                        <p className="empty-state-text">Try a different search or filter.</p>
                    </div>
                )}
                </>}
            </div>

            {/* Step Detail Slideout Panel */}
            {selectedStep && (
                <StepDetailPanel step={selectedStep} onClose={() => setSelectedStep(null)} />
            )}
        </>
    );
}

export default Automations;
